// WARNING: THIS FILE IS SHARED TO BOTH CLIENT AND SERVER

export const TEXT_CARD_TYPE = "text";
export const LABEL_CARD_TYPE = "label";
export const OUTLINE_CARD_TYPE = "outline";
export const OUTLINE_HEADER_CARD_TYPE = "outline_header";

export const CANVAS_OUTLINE_HEROS_JOURNEY_ID = "heros";
export const CANVAS_OUTLINE_SAVE_THE_CAT_ID = "cat";
export const CANVAS_OUTLINE_ROMANCING_THE_BEAT_ID = "romance";
export const CANVAS_OUTLINE_STORY_CIRCLE_ID = "story-circle";

export const CANVAS_OUTLINE_TYPES = [
  {
    label: "The Hero's Journey",
    value: CANVAS_OUTLINE_HEROS_JOURNEY_ID,
    great: "Any story with a main protagonist who undergoes a transformative change over the course of the story.",
    examples: [
      "Katniss volunteers to participate in the deadly Hunger Games in order to save her younger sister, Primrose. She navigates the challenges of the games, forming alliances and facing danger, ultimately defeating the other tributes and the gamemakers to become the victor. However, her triumph comes with consequences and she must now face the aftermath of her actions.",
      "Thomas Anderson, a computer programmer, discovers the truth about the simulated reality known as the Matrix and joins a rebellion to overthrow the sentient machines that control it. He trains with Morpheus and learns to control the Matrix, ultimately sacrificing himself to save humanity and defeat the machines.",
      "A young farm boy named Luke Skywalker joins forces with rogue smuggler Han Solo and two droids to rescue Princess Leia from the evil Galactic Empire and destroy the Death Star. Along the way, he trains with Jedi master Obi-Wan Kenobi, learns the ways of the Force and confronts Darth Vader, ultimately saving the galaxy from the Empire's tyranny.",
    ],
  },
  {
    label: "Hollywood Beats",
    value: CANVAS_OUTLINE_SAVE_THE_CAT_ID,
    great: "Stories that focus on a protagonist overcoming obstacles to achieve a goal and restore balance to a chaotic situation. Compare to: Save the Cat",
    examples: [
      "Forrest, a slow-witted but kind-hearted man, becomes involved in some of the defining events of the 20th century. He saves a young girl's life on a school bus, winning her lifelong friendship, and enlists in the army to serve in the Vietnam War. Through his experiences, he learns to find happiness and love, ultimately returning home to his best friend, Jenny.",
      "An elderly man named Carl attaches thousands of balloons to his house and sets off on an adventure to fulfill his lifelong dream of visiting South America. Along the way, he saves a young wilderness explorer named Russell, and together they embark on a journey filled with danger and discovery. Ultimately, Carl learns to let go of his past and embrace the present, discovering a new family and a renewed sense of purpose.",
      "A group of toys, led by Woody the cowboy, must come to terms with the arrival of a new toy, Buzz Lightyear, and find a way to get back to their owner, Andy. Along the way, they face numerous obstacles and make new friends, ultimately saving one another and reuniting with Andy in time for his move to a new home.",
    ],
    preamble:
      "The beginning and end of a good fiction story are connected, forming a narrative cycle with the end providing closure and resolution to the story problem, tying back to the beginning. The end should offer a sense of satisfaction and completion to the reader. If the main character defeats the story problem, this is a happy ending. If the main character failed to overcome the story problem, this is a tragic ending.",
  },
  {
    label: "Story Circle",
    value: CANVAS_OUTLINE_STORY_CIRCLE_ID,
    great: "Stories that have a universal theme and follow a circular narrative structure, with a beginning, middle, and end that are all connected.",
    examples: [
      "The head of a powerful mafia family, Vito Corleone, must navigate a power struggle within the family and defend his empire from rival gangs. Through his experiences, he learns to balance power and loyalty, ultimately ensuring the continued success and protection of his family.",
      "A young girl named Dorothy is swept away by a tornado to a magical land and must find her way back home. Along the way, she makes new friends and faces challenges, ultimately learning to believe in herself and finding the courage to return home.",
      "A banker named Andy Dufresne is wrongly convicted of murder and sent to Shawshank prison. He befriends a fellow inmate named Red and adapts to prison life, eventually discovering a way to escape and start a new life. Through his experiences, he learns to find hope and redemption, ultimately finding freedom and justice.",
    ],
  },
  {
    label: "Romance Outline",
    value: CANVAS_OUTLINE_ROMANCING_THE_BEAT_ID,
    great: "Romance stories that focus on the development of a relationship between two characters as they face obstacles and challenges. Compare to: Romancing the Beat",
    examples: [
      "Anastasia Steele begins a relationship with young business magnate Christian Grey and they navigate their differences and the challenges of their relationship to ultimately fall in love and build a life together.",
      "Set in Regency England, this story follows the Bridgerton siblings as they navigate the complexities of love during the Ton's social season. The eldest sibling, Daphne, is determined to secure a suitable marriage, but when she meets the Duke of Hastings, they must navigate their differences and the expectations of their social circles.",
      "When a British army nurse named Claire travels back in time to 18th century Scotland, she finds herself torn between her husband in the 20th century and a dashing Scottish warrior named Jamie. Through their experiences and conflicts, they must navigate the difficulties of their relationship and the challenges of their historical time period, but find love and a path to happiness together.",
    ],
    preamble:
      "A romance novel has a primary plot of two or more people falling in love. In a romance novel, plots other than the characters falling in love are secondary and connected to the main plot of falling in love. Romance novels always end in a happily ever after or a happy for now situation for the characters.",
  },
];

export const CANVAS_OUTLINE_BEATS = {
  [CANVAS_OUTLINE_HEROS_JOURNEY_ID]: [
    {
      id: 0,
      title: "Part 1: The Ordinary World",
      description: "Establish who the hero is and what their normal life is like.",
      prompt: "Establish who the hero is and what their normal life is like. Specific details about their normal life, what they do, what they're good at, etc",
      rejects: ["Ordinary"],
    },
    {
      id: 1,
      title: "Part 2: The Call to Adventure",
      description: "Something happens that forces the hero to step outside their comfort zone.",
      prompt: "Something happens that forces the hero to step outside their comfort zone, with serious consequences if they refuse. Give specific details about this force and how the hero is tested",
      rejects: ["Call"],
    },
    {
      id: 2,
      title: "Part 3: Refusal of the Call",
      description: "The hero initially refuses the call, because it is too far out of their confort zone.",
      prompt:
        "The hero initially refuses the call due to fear, doubt, or a sense of obligation to their current life. It should also serve to add complexity to the protagonist's character and create dramatic tension. Give specific details about their fear and how they refuse.",
      rejects: ["Refusal"],
    },
    {
      id: 3,
      title: "Part 4: Meeting the Mentor",
      description: "The hero meets a wise figure who teaches the hero something that makes them ready accept the call to action.",
      prompt:
        "The hero interacts with someone who teaches the hero something or helps them see something in a new light, which makes them ready accept the call to action. Give specific details about the mentor. Don't just make them a 'wise old figure'",
      rejects: ["Mentor"],
    },
    {
      id: 4,
      title: "Part 5: Crossing the Threshold",
      description: "The hero takes the first step from the ordinary world into the special world.",
      prompt: "The hero takes the first step from the ordinary world into the special world. Give specific details about the special world.",
      rejects: ["Crossing", "Threshold"],
    },
    {
      id: 5,
      title: "Part 6: Tests, Allies, Enemies",
      description: "The Hero meets people who will help or harm them, and learns the rules of the Special World.",
      prompt: "The Hero meets people who will help or harm them, and learns the rules of the Special World. Give specific details about the harm and the people.",
      rejects: ["Tests", "Enemies", "Allies"],
    },
    {
      id: 6,
      title: "Part 7: Approaching the Inner Cave",
      description: "The Hero must prepare or evolve in order to tackle the Journey’s heart or central ordeal.",
      prompt: "The Hero must prepare or evolve in order to tackle the Journey’s heart or central ordeal. Give specific details about the ordeal and how the hero evolves.",
      rejects: ["Cave", "Approaching"],
    },
    {
      id: 7,
      title: "Part 8: The Ordeal",
      description: "The hero faces the central life-or-death challenge and experiencing “death”.",
      prompt: "The hero faces the central life-or-death challenge and experiencing “death”.  Make sure to provide specifics.",
      rejects: ["Ordeal"],
    },
    {
      id: 8,
      title: "Part 9: The Reward",
      description: "The Hero survives “death” at great cost, earning the reward they first sought.",
      prompt: "The Hero survives death at great cost, earning the reward they first sought.  Make sure to provide specifics.",
      rejects: ["Reward"],
    },
    {
      id: 9,
      title: "Part 10: The Road back Home",
      description: "The hero is forced to return to the ordinary world.",
      prompt: "The hero is forced to return to the ordinary world. Make sure to provide specifics.",
      rejects: ["Road"],
    },
    {
      id: 10,
      title: "Part 11: Atonement",
      description: "The final test of the Hero’s transformation, they demonstrate mastery of their newfound abilities",
      prompt: "The final test of the Hero’s transformation, they demonstrate mastery of their newfound abilities. Make sure to provide specifics.",
      rejects: ["Atonement"],
    },
    {
      id: 11,
      title: "Part 12: Return with the Elixir",
      description: "The hero returns with the love, wisdom, magic, etc. to heal a wounded land in the ordinary world.",
      prompt: "The hero returns with the love, wisdom, magic, etc. to heal a wounded land in the ordinary world. Make sure to provide specifics.",
      rejects: ["Elixir"],
    },
  ],
  [CANVAS_OUTLINE_SAVE_THE_CAT_ID]: [
    {
      id: 0,
      title: "Part 1: Main Character Sketch",
      description: "Give a backstory and details about the main character that are important to the theme and story problem.",
      prompt:
        "Give a backstory and details about the main character that are important to the theme and story problem. Unique qualities combined with genre tropes are ideal, as well as flaws. Provide specific details.",
      rejects: ["Main", "Sketch"],
    },
    {
      id: 1,
      title: "Part 2: Universal Theme",
      description: "Introduce the theme of the story, setting the tone and story problem. Provide specific details about the theme and how it’s introduced through the lens of the character sketch.",
      prompt:
        "The theme of the story is introduced, setting the tone and story problem. Provide specific details about the theme and how it’s introduced through the lens of the character sketch. The theme should be a concept that resonates with many readers’ personal experiences, hopes, or fantasies.",
      rejects: ["Theme"],
    },
    {
      id: 2,
      title: "Part 3: Story Problem",
      description: "Introduce side characters and settings and show the main character in their daily life including their ties to their backstory and personalit.",
      prompt:
        "Introduce side characters and settings with detail and show the main character in their daily life including their ties to their backstory and personality. Introduce the story problem that relates to the theme. This should span multiple scenes in the story and be a progression of story narrative.",
      rejects: ["Story"],
    },
    {
      id: 3,
      title: "Part 4: Story Spark",
      description: "An event or realization that forces the main character to commit to solving the story problem.",
      prompt:
        "An event or realization that forces the main character to commit to solving the story problem. This plot point should relate to the story theme and be a compelling and convincing reason why the main character cannot simply ignore the story problem.",
      rejects: ["Spark"],
    },
    {
      id: 4,
      title: "Part 5: Too Many Options",
      description: "The main character is confused over more than one way to solve the story problem and must make a decision.",
      prompt:
        "The main character is confused over more than one way to solve the story problem and must make a decision about which is the best way to choose. The story should have specific story events, other characters, or the main character show the reader the pros and cons of each way to solve the story problem and some of the drawbacks should relate to the main character’s flaw.",
      rejects: ["Options"],
    },
    {
      id: 5,
      title: "Part 6: First Choice",
      description: "The main character and supporting characters make a choice on how to solve the story problem.",
      prompt:
        "The main character and any appropriate supporting characters make a choice on how to solve the story problem. This choice should be a defining story event moment and it should make the story problem worse or more difficult to solve and relate to the B plot.",
      rejects: ["Choice"],
    },
    {
      id: 6,
      title: "Part 7: Additional Characters",
      description: "Introduction of a new character that will be key to the main character solving the story problem and a related B plot.",
      prompt:
        "Introduction of a new character that will be key to the main character solving the story problem and a related B plot. In the process of solving the story problem, the new character and the main character develop some type of relationship to each other.",
      rejects: ["Additional"],
    },
    {
      id: 7,
      title: "Part 8: New Life",
      description: "A multiple scene section where the main character is either making progress on solving the story problem or failing miserably.",
      prompt: "A multiple scene section where the main character is either making progress on solving the story problem or failing miserably. This depends on the tone, theme, and genre of the story.",
      rejects: ["Life"],
    },
    {
      id: 8,
      title: "Part 9: False Ending",
      description:
        "A false victory for the main character if they have seemed to make progress against the problem or a false defeat if they have seemed to be failing to make progress against the problem.",
      prompt:
        "A false victory for the main character if they have seemed to make progress against the story problem or a false defeat for the main character if they have seemed to be failing to make progress against the story problem.",
      rejects: ["False"],
    },

    {
      id: 9,
      title: "Part 10: Change of Circumstances",
      description: "If the main character had a false victory the problem becomes even harder to solve or if they had a false defeat the problem appears easier to solve.",
      prompt:
        "Opposite of Part 9: False Ending, if the main character had a false victory now the story problem becomes even harder to solve or if the main character had a false defeat the story problem appears easier to solve.",
      rejects: ["Circumstances"],
    },
    {
      id: 10,
      title: "Part 11: Reset of Progress",
      description: "The main character seems to lose all progress made against solving the story problem. This should relate to a consequence of the choices made earlier.",
      prompt:
        "The main character seems to lose all progress made against solving the story problem. This should relate to a consequence of the choices made earlier in the story and potentially relate to the character’s flaws.",
      rejects: ["Progress"],
    },
    {
      id: 11,
      title: "Part 12: Rock Bottom",
      description: "The main character is shown to be at the lowest point emotionally, as far as allies to solve the story problem go, and resources.",
      prompt:
        "The main character is shown to be at the lowest point emotionally, as far as allies to solve the story problem go, and resources. The main character’s flaws could influence this plot point. The reader should believe that the story will not have the ending expected of the genre.",
      rejects: ["Rock"],
    },
    {
      id: 12,
      title: "Part 13: Better Choice",
      description: "The main character takes an inventory of everything that has happened so far to them in the story and figures out a new way to solve the story problem.",
      prompt:
        "The main character takes an inventory of everything that has happened so far to them in the story and figures out a new way to solve the story problem. The main character should commit to the lesson of the story’s theme and explain the new plan to attack the story problem, overcoming their flaws.",
      rejects: ["Choice"],
    },
    {
      id: 13,
      title: "Part 14: Attacking the Story Problem",
      description: "The main character gathers all allies and resources to face the story problem directly, once and for all, usually as part of resolution to the B plot.",
      prompt:
        "The main character gathers all allies, side characters, new characters, and resources to face the story problem directly, once and for all, usually as part of resolution to the B plot. The main character begins to execute the plan and some sacrifices should be made, a new threat emerges and the main character adjusts based on the growth they’ve made in the story. It can be clear or unclear at the end of this plot point if the main character prevailed.",
      rejects: ["Attacking"],
    },
    {
      id: 14,
      title: "Part 15: New Main Character Sketch",
      description: "This is a mirror of Part 1 that shows the main character in their new reality. If they defeat the problem, this is a happy ending. If they failed, this is a tragic ending.",
      prompt:
        "This is a mirror of Part 1: Main Character Sketch that shows the main character in their new reality after attacking the story problem. If the main character defeats the story problem, this is a happy ending. If the main character failed to overcome the story problem, this is a tragic ending.",
      rejects: ["Sketch"],
    },
  ],
  [CANVAS_OUTLINE_ROMANCING_THE_BEAT_ID]: [
    {
      id: 0,
      title: "Part 1: Ordinary World",
      description: "Establish at least one character in the romance in that character’s ordinary world",
      prompt: "Establish at least one character in the romance in that character’s ordinary world. Specific details about their personality, interests, goals, etc.",
      rejects: ["Ordinary"],
    },
    {
      id: 1,
      title: "Part 2: First Encounter",
      description: "A unique meeting between two or more characters in the romance.",
      prompt: "A unique meeting between two or more characters in the romance. Specific details about the meeting.",
      rejects: ["Encounter"],
    },
    {
      id: 2,
      title: "Part 3: Emotional Stir",
      description: "A situation that results in a strong emotional reaction in one or more characters in the romance.",
      prompt: "A situation that results in a strong emotional reaction in one or more characters in the romance. Give specific details about the situation.",
      rejects: ["Emotional", "Stir"],
    },
    {
      id: 3,
      title: "Part 4: Secondary Plot Twist",
      description: "Introduce a B plot story problem for one or more of the characters in the romance.",
      prompt: "Introduce a B plot story problem for one or more of the characters in the romance. Give specific details about the conflict.",
      rejects: ["Secondary", "Twist"],
    },
    {
      id: 4,
      title: "Part 5: Unbreakable Ties",
      description: "A compelling reason why the characters in the romance cannot simply never see each other again.",
      prompt: "A compelling reason why the characters in the romance cannot simply never see each other again. Give specific details about the event.",
      rejects: ["Unbreakable Ties"],
    },
    {
      id: 5,
      title: "Part 6: Romantic Gesture",
      description: "A romantic act or gesture occurs between two or more characters in the romance.",
      prompt: "A romantic act or gesture occurs between two or more characters in the romance. Give specific details about the gesture.",
      rejects: ["Romantic", "Gesture"],
    },
    {
      id: 6,
      title: "Part 7: Obstacle Arises",
      description: "A problem from the B plot creates an obstacle to further development of the romantic relationship.",
      prompt: "A problem from the B plot creates an obstacle to further development of the romantic relationship. Give specific details.",
      rejects: ["Obstacle"],
    },
    {
      id: 7,
      title: "Part 8: Problem-Solving Partnership",
      description: "The Characters in the romance relationship struggle to find a solution to the problem from the B plot.",
      prompt: "The Characters in the romance relationship struggle to find a solution to the problem from the B plot. Provide specific details.",
      rejects: ["Problem", "Partnership"],
    },
    {
      id: 8,
      title: "Part 9: Doubt and Despair",
      description: "One or more characters in the romance believes the romance is over or impossible to repair.",
      prompt: "One or more characters in the romance believes the romance is over or impossible to repair. Provide specific details.",
      rejects: ["Doubt", "Despair"],
    },
    {
      id: 9,
      title: "Part 10: Grand Romantic Declaration",
      description: "One or more characters is inspired to make a grand romantic gesture to overcome the problem from the B plot.",
      prompt: "One or more characters is inspired to make a grand romantic gesture to overcome the problem from the B plot. Give specific details.",
      rejects: ["Doubt", "Despair"],
    },
    {
      id: 10,
      title: "Part 11: Happy Resolution",
      description: "The characters in the romance work together to solve any final obstacles that remain and enjoy a highly intimate romantic moment.",
      prompt: "The characters in the romance work together to solve any final obstacles that remain and enjoy a highly intimate romantic moment. Give specific details.",
      rejects: ["Happy Resolution"],
    },
    {
      id: 11,
      title: "Part 12: Ever After",
      description: "The characters in the romance live happily ever after or happily for now.",
      prompt: "The characters in the romance live happily ever after or happily for now. Give specific details about the ending.",
      rejects: ["Ever"],
    },
  ],
  [CANVAS_OUTLINE_STORY_CIRCLE_ID]: [
    {
      id: 0,
      title: "Part 1: The Zone of Comfort",
      description: "A character is in a zone of comfort.",
      prompt: "Establish who the character is and describe their everyday life as mundane and unchallenging. Give specific details about their comfort zone and how it is unchallenging.",
      rejects: ["Comfort"],
    },
    {
      id: 1,
      title: "Part 2: The Desire",
      description: "The protagonist’s desire compels them to take action.",
      prompt: "Describe the protagonist's desire and how it compels them to take action. Give specific details about what they want and how their desire drives their actions.",
      rejects: ["Desire"],
    },
    {
      id: 2,
      title: "Part 3: The Opportunity",
      description: "The protagonist recognizes a unique opportunity to fulfill their desire.",
      prompt: "Describe how the protagonist recognizes a unique opportunity to fulfill their desire. Give specific details about the opportunity and how it presents itself.",
      rejects: ["Opportunity"],
    },
    {
      id: 3,
      title: "Part 4: The Choice",
      description: "The protagonist decides to pursue their desire by choosing to act.",
      prompt: "Describe how the protagonist decides to pursue their desire by choosing to act. Give specific details about their decision and the circumstances surrounding it.",
      rejects: ["Choice"],
    },
    {
      id: 4,
      title: "Part 5: The Actions",
      description: "The protagonist takes specific actions to fulfill their desire.",
      prompt: "Describe the specific actions the protagonist takes to fulfill their desire. Give details about the actions and the consequences of those actions.",
      rejects: ["Actions"],
    },
    {
      id: 5,
      title: "Part 6: The Consequence",
      description: "The protagonist experiences consequences as a result of their actions.",
      prompt: "Describe the consequences the protagonist experiences as a result of their actions. Give specific details about the consequences and how they affect the protagonist.",
      rejects: ["Consequence"],
    },
    {
      id: 6,
      title: "Part 7: The New Reality",
      description: "The protagonist is faced with a new reality as a result of the consequences of their actions.",
      prompt:
        "Describe the new reality the protagonist is faced with as a result of the consequences of their actions. Give specific details about the new reality and how it affects the protagonist.",
      rejects: ["Reality"],
    },
    {
      id: 7,
      title: "Part 8: The Resolution",
      description: "The protagonist resolves to accept their new reality and finds peace with it.",
      prompt: "Describe how the protagonist resolves to accept their new reality and finds peace with it. Give specific details about their resolution and the steps they take to achieve peace.",
      rejects: ["Resolution"],
    },
  ],
};
